import { useState, useEffect } from "react";
import { EyeOutlined, EyeInvisibleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import UserProfileAPI from "../services/UserProfileAPI";
import type { Route } from "./+types/login";

export function meta({}: Route.MetaArgs) {
  return [
    { title: "Register - Atila" },
    { name: "description", content: "Create your Atila account" },
  ];
}

export default function Register() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    password: "",
    confirm_password: "",
    account_type: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [userData, setUserData] = useState<null | {
    id: string;
    username: string;
    token: string;
    user_profile: any;
  }>(null);
  const [isClient, setIsClient] = useState(false);

  // Fix for handling localStorage in SSR
  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear specific field error when user types
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Reset success state if user tries to change data after successful registration
    if (registerSuccess) setRegisterSuccess(false);
  };

  const handleAccountTypeSelect = (type: string) => {
    setFormData((prev) => ({ ...prev, account_type: type }));
    if (errors.account_type) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.account_type;
        return newErrors;
      });
    }
  };

  const togglePasswordVisibility = (field: 'password' | 'confirm_password') => {
    if (field === 'password') {
      setShowPassword((prev) => !prev);
    } else {
      setShowConfirmPassword((prev) => !prev);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    // Validate first name
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }
    
    // Validate last name
    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }
    
    // Validate username
    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    } else if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }
    
    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    
    // Validate password
    if (!formData.password) {
      newErrors.password = "Password is required";
    }
    
    // Validate confirm password
    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }
    
    // Validate account type
    if (!formData.account_type) {
      newErrors.account_type = "Please select if you are a mentor or mentee";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Prepare data for API request
      const registerData = {
        user: {
            username: formData.username,
            email: formData.email,
            password: formData.password
        },
        userProfile: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          account_type: formData.account_type
        },
        locationData: {},
      };
      
      // Use the UserProfileAPI to handle registration
      const response = await UserProfileAPI.createUser(registerData);
      const data = response.data;
      
      // Store user data in localStorage (only in client)
      if (isClient) {
        localStorage.setItem("atilaToken", data.token);
        localStorage.setItem("atilaUser", JSON.stringify({
          id: data.id,
          username: data.username,
          user_profile: data.user_profile
        }));
        UserProfileAPI.authenticateRequests();
      }
      
      // Show success message
      setRegisterSuccess(true);
      setUserData(data);
      
      // Redirect based on account type
      setTimeout(() => {
        window.location.href = formData.account_type === "mentor" 
          ? `/profile/${data.username}` 
          : "/mentorship";
      }, 1000);
      
    } catch (err: any) {
      // Handle Axios error
      const errorResponse = err.response?.data || {};
      
      // Server validation errors
      if (errorResponse.username) {
        setErrors(prev => ({ ...prev, username: errorResponse.username[0] }));
      }
      if (errorResponse.email) {
        setErrors(prev => ({ ...prev, email: errorResponse.email[0] }));
      }
      if (errorResponse.password) {
        setErrors(prev => ({ ...prev, password: errorResponse.password[0] }));
      }
      if (errorResponse.non_field_errors) {
        setErrors(prev => ({ ...prev, general: errorResponse.non_field_errors[0] }));
      }
      
      // Generic error
      if (!errorResponse.username && !errorResponse.email && !errorResponse.password && !errorResponse.non_field_errors) {
        setErrors(prev => ({ 
          ...prev, 
          general: err.message || "Registration failed. Please try again." 
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50 p-4 dark:bg-gray-900">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Create an Atila Account</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Join our community and start your journey
          </p>
        </div>
        
        {registerSuccess && userData ? (
          <div className="rounded-lg bg-white p-8 shadow-md dark:bg-gray-800">
            <div className="mb-4 rounded-md bg-green-50 p-4 dark:bg-green-900/20">
              <div className="flex items-center">
                <CheckCircleOutlined className="mr-2 text-lg text-green-600 dark:text-green-400" />
                <p className="text-sm font-medium text-green-800 dark:text-green-300">
                  Registration successful!
                </p>
              </div>
            </div>
            
            <div className="mt-4 space-y-2">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                Welcome, {userData.username}!
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Your account has been created successfully.
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                Redirecting you to {formData.account_type === "mentor" ? "complete your profile" : "the mentorship page"}...
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="rounded-lg bg-white p-8 shadow-md dark:bg-gray-800">
            {errors.general && (
              <div className="mb-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20">
                <p className="text-sm text-red-700 dark:text-red-400">{errors.general}</p>
              </div>
            )}
            
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label 
                  htmlFor="first_name" 
                  className="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-200"
                >
                  First Name
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.first_name ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400`}
                  placeholder="First name"
                />
                {errors.first_name && (
                  <p className="mt-1 text-xs text-red-600">{errors.first_name}</p>
                )}
              </div>
              <div>
                <label 
                  htmlFor="last_name" 
                  className="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-200"
                >
                  Last Name
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.last_name ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400`}
                  placeholder="Last name"
                />
                {errors.last_name && (
                  <p className="mt-1 text-xs text-red-600">{errors.last_name}</p>
                )}
              </div>
            </div>
            
            <div className="mb-4">
              <label 
                htmlFor="username" 
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-200"
              >
                Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className={`w-full rounded-md border ${errors.username ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400`}
                placeholder="Choose a username"
              />
              {errors.username && (
                <p className="mt-1 text-xs text-red-600">{errors.username}</p>
              )}
            </div>
            
            <div className="mb-4">
              <label 
                htmlFor="email" 
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-200"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full rounded-md border ${errors.email ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400`}
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email}</p>
              )}
            </div>
            
            <div className="mb-4">
              <label 
                htmlFor="password" 
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-200"
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.password ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400`}
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('password')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeInvisibleOutlined className="text-lg" />
                  ) : (
                    <EyeOutlined className="text-lg" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-600">{errors.password}</p>
              )}
            </div>
            
            <div className="mb-4">
              <label 
                htmlFor="confirm_password" 
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-200"
              >
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirm_password"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.confirm_password ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm_password')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <EyeInvisibleOutlined className="text-lg" />
                  ) : (
                    <EyeOutlined className="text-lg" />
                  )}
                </button>
              </div>
              {errors.confirm_password && (
                <p className="mt-1 text-xs text-red-600">{errors.confirm_password}</p>
              )}
            </div>
            
            <div className="mb-6">
              <label 
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-200"
              >
                I am a
              </label>
              <div className="flex gap-4">
                <div 
                  onClick={() => handleAccountTypeSelect("mentor")}
                  className={`flex-1 cursor-pointer rounded-md border p-4 transition ${
                    formData.account_type === "mentor" 
                      ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20" 
                      : "border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/30"
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="mentor"
                      name="account_type" 
                      checked={formData.account_type === "mentor"}
                      onChange={() => handleAccountTypeSelect("mentor")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 dark:text-blue-400"
                    />
                    <label 
                      htmlFor="mentor" 
                      className="ml-2 block cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-200"
                    >
                      Mentor
                    </label>
                  </div>
                </div>
                
                <div 
                  onClick={() => handleAccountTypeSelect("mentee")}
                  className={`flex-1 cursor-pointer rounded-md border p-4 transition ${
                    formData.account_type === "mentee" 
                      ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20" 
                      : "border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/30"
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="mentee"
                      name="account_type"
                      checked={formData.account_type === "mentee"}
                      onChange={() => handleAccountTypeSelect("mentee")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 dark:text-blue-400"
                    />
                    <label 
                      htmlFor="mentee" 
                      className="ml-2 block cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-200"
                    >
                      Mentee
                    </label>
                  </div>
                </div>
              </div>
              {errors.account_type && (
                <p className="mt-1 text-xs text-red-600">{errors.account_type}</p>
              )}
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-md bg-blue-600 py-2"
              style={{color: "white", cursor: "pointer"}}
            >
              {isLoading ? "Creating account..." : "Create Account"}
            </button>
            
            <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
              Already have an account?{" "}
              <a href="/login" className="text-blue-600 hover:underline dark:text-blue-400">
                Login
              </a>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}